import { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import './style.scss';
import RequiredAsterisk from '../RequiredAsterisk';
import { SanitizerService } from '../../../services/sanitizer.service';
import ValidationInfo from '../../../validation/validation-info';
import type IFile from './models/file.model';
import { TranslationService } from '../../../services/translation.service';
import type IValidatable from '../validatable.interface';
import type { IServerValidationError } from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';

const maxFileSize = 15728640;
const maxNrOffFiles = 6;
const serverValidationName = 'uploadedFiles'; // For backwards compatibility

export interface IFileUploadState {
  value: IFile[];
  validationInfo: ValidationInfo;
}

export interface IFileUploadProps {
  id: string;
  isRequired?: boolean;
  infoText?: string;
  state: IFileUploadState;
  legendText?: string;
  onFilesAdded?: (files: IFile[]) => void;
  onFileRemoved?: (file: IFile) => void;
}

export default forwardRef<IValidatable, IFileUploadProps>(function FileUpload({ id, isRequired = false, infoText, state, legendText, onFilesAdded = undefined, onFileRemoved = undefined }, ref) {
  // === STATE ===
  const [showValidFileFormats, setShowValidFileFormats] = useState(false);

  // === REFERENCES ===
  const fileInputRef = useRef<HTMLInputElement>(null);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${TranslationService.get('common.addfiles')} - `);
      ValidationEngine.for(updatedValidationInfo)
        .requiredIf(isRequired, state.value.length > 0)
        .maxLength(state.value, maxNrOffFiles)
        .lessThan(
          state.value.reduce((acc, curr) => acc + curr.size, 0),
          maxFileSize + 1,
          'common.filesizeexceeded'
        )
        .validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${TranslationService.get('common.addfiles')} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), [])
      );
    },
  }));

  function handleFileInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const result: IFile[] = [];
    const files = event.currentTarget.files;
    if (files) {
      for (let i = 0; i < files.length; i += 1) {
        const file = files.item(i);
        if (file) {
          result.push({
            name: file.name,
            size: file.size,
            displaySize: file.size === 0 ? '0 MB' : `${parseFloat((file.size / 1048576).toFixed(1))} MB`,
            actualFile: file,
          });
        }
      }
    }
    onFilesAdded?.(result);
  }

  function handleRemoveFileClick(index: number): void {
    onFileRemoved?.(state.value[index]);
  }

  function handleLabelKeyDown(event: React.KeyboardEvent<HTMLLabelElement>): void {
    if (event.key === 'Enter') {
      fileInputRef.current?.click();
    }
  }

  return (
    <div id={id} className="file-upload">
      <div className="mb-3">
        <fieldset>
          <legend>
            {legendText ?? TranslationService.get('common.uploadfiles')} {isRequired && <RequiredAsterisk />}
          </legend>
          <p>{infoText ?? TranslationService.get('common.uploadfilesinfo')}</p>
          <div className="mb-3">
            <button
              type="button"
              className={`btn--as-link btn--with-caret ${showValidFileFormats ? '' : 'collapsed'}`}
              aria-expanded={showValidFileFormats ? 'true' : 'false'}
              aria-controls={`${id}-valid-file-formats-list`}
              onClick={() => {
                setShowValidFileFormats(!showValidFileFormats);
              }}
            >
              {TranslationService.get('common.showlistofvalidfileformats')}
            </button>
          </div>
          <div
            id={`${id}-valid-file-formats-list`}
            hidden={!showValidFileFormats}
            dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('common.validfileformats')) }}
          ></div>
          {/* The label needs role="button" to make the screen reader understand that the label has button functionality, we ignore the validation error in W3 HTML Validator, see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
          <label role="button" tabIndex={0} htmlFor={`${id}-file-upload-input`} onKeyDown={handleLabelKeyDown} className="btn btn-secondary file-upload__input-label mb-0">
            {TranslationService.get('common.addfiles')}
          </label>
          <input
            tabIndex={-1}
            ref={fileInputRef}
            id={`${id}-file-upload-input`}
            className={`d-none ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
            type="file"
            multiple
            onChange={handleFileInputChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.gif,.png,.txt"
          />
        </fieldset>
        {state.validationInfo.errors.map((error, index) => (
          <div key={index} className="invalid-feedback">
            {error}
          </div>
        ))}
        {state.value.length > 0 && (
          <div>
            <ul id={`${id}-uploaded-files-list`}>
              {state.value.map((file, index) => (
                <li key={index} className="custom-icon">
                  {file.name} ({file.displaySize})
                  <button type="button" className="btn--as-link" onClick={() => handleRemoveFileClick(index)}>
                    <p className="mb-0">{TranslationService.get('common.removefile')}</p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
});
